import {StateMachine} from 'javascript-state-machine';

import type {HeartbeatService} from '../../core/HeartbeatService';
import {Event, JSM_INITIAL_EVENT} from '../../enums/Event';
import {createVideoStartFailedReason} from '../../enums/VideoStartFailedReason';
import type {AnalyticsStateMachineOptions} from '../../types/AnalyticsStateMachineOptions';
import type {AnalyticsEventBase, ErrorEvent} from '../../types/EventData';
import type {StateMachineCallbacks} from '../../types/StateMachineCallbacks';
import {isNullish, isNumber} from '../../utils/Utils';
import {AnalyticsStateMachine} from '../AnalyticsStateMachine';
import {customStateMachineErrorCallback, logStateMachineTransition, on} from '../stateMachineUtils';

enum State {
  ERROR = 'ERROR',
  PAUSE = 'PAUSE',
  PLAYING = 'PLAYING',
  QUALITY_CHANGE = 'QUALITY_CHANGE',
  READY = 'READY',
  REBUFFERING = 'REBUFFERING',
  SEEKING = 'SEEKING',
  SETUP = 'SETUP',
  STARTUP = 'STARTUP',
  UNLOADED = 'UNLOADED',
  VIDEOSTART_FAILED = 'VIDEOSTART_FAILED',
}

export class AmazonIVSStateMachine extends AnalyticsStateMachine {
  constructor(
    stateMachineCallbacks: StateMachineCallbacks,
    playingHeartbeatService: HeartbeatService,
    opts: AnalyticsStateMachineOptions,
  ) {
    super(stateMachineCallbacks, playingHeartbeatService, opts);
  }

  override createStateMachine(opts: AnalyticsStateMachineOptions): StateMachine {
    return StateMachine.create({
      // custom initial state and custom JSM initial event name
      // initial event is used by JSM for the initial state transition 'none' -> 'SETUP'
      initial: {state: State.SETUP, event: JSM_INITIAL_EVENT},
      error: customStateMachineErrorCallback,
      events: [
        // STARTUP
        {name: Event.READY, from: State.SETUP, to: State.READY},
        {name: Event.PLAY, from: State.READY, to: State.STARTUP},
        {name: Event.PLAYING, from: State.STARTUP, to: State.PLAYING},

        // PLAYBACK
        {name: Event.PAUSE, from: State.PLAYING, to: State.PAUSE},
        {name: Event.PLAYING, from: State.PAUSE, to: State.PLAYING},
        {name: Event.PLAY, from: State.PAUSE, to: State.PLAYING},
        {name: Event.UNLOAD, from: State.PLAYING, to: State.UNLOADED},
        {name: Event.PLAYING, from: State.ERROR, to: State.PLAYING}, // recover from error and continue playing
        {name: Event.VIDEO_CHANGE, from: State.PLAYING, to: State.QUALITY_CHANGE},
        {name: 'FINISH_QUALITYCHANGE', from: State.QUALITY_CHANGE, to: State.PLAYING},

        // SEEKING
        {name: Event.SEEK, from: State.PLAYING, to: State.SEEKING},
        {name: Event.SEEKED, from: State.SEEKING, to: State.PLAYING},
        {name: Event.PAUSE, from: State.SEEKING, to: State.PAUSE},

        // BUFFERING
        {name: Event.START_BUFFERING, from: State.PLAYING, to: State.REBUFFERING},
        {name: Event.PLAYING, from: State.REBUFFERING, to: State.PLAYING},

        // VIDEOSTART_FAILED
        {name: Event.ERROR, from: State.STARTUP, to: State.VIDEOSTART_FAILED},
        {name: Event.UNLOAD, from: State.STARTUP, to: State.VIDEOSTART_FAILED},
        {name: Event.VIDEOSTART_TIMEOUT, from: State.STARTUP, to: State.VIDEOSTART_FAILED},

        // ERROR
        {name: Event.ERROR, from: getAllStatesBut([State.STARTUP, State.VIDEOSTART_FAILED]), to: State.ERROR},

        // EVENTS TO IGNORE
        on(Event.PAUSE).stayIn(State.PAUSE),
        on(Event.PAUSE).stayIn(State.STARTUP),
        on(Event.PAUSE).stayIn(State.REBUFFERING),
        on(Event.PLAY).stayIn(State.PLAYING),
        on(Event.PLAYING).stayIn(State.PLAYING),
        on(Event.START_BUFFERING).stayIn(State.PAUSE),
        on(Event.START_BUFFERING).stayIn(State.REBUFFERING),
        on(Event.VIDEOSTART_TIMEOUT).stayIn(State.PLAYING),
        on(Event.VIDEOSTART_TIMEOUT).stayIn(State.PAUSE),
        on(Event.VIDEOSTART_TIMEOUT).stayIn(State.SEEKING),
        on(Event.UNLOAD).stayIn(State.READY),
        on(Event.UNLOAD).stayIn(State.SEEKING),

        on(Event.VIDEO_CHANGE).stayIn(State.QUALITY_CHANGE),
        on(Event.VIDEO_CHANGE).stayIn(State.STARTUP),
        on(Event.VIDEO_CHANGE).stayIn(State.REBUFFERING),
        on(Event.VIDEO_CHANGE).stayIn(State.PAUSE),
        on(Event.VIDEO_CHANGE).stayIn(State.ERROR),
        on(Event.VIDEO_CHANGE).stayIn(State.VIDEOSTART_FAILED),
        on(Event.VIDEO_CHANGE).stayIn(State.READY),
        on(Event.VIDEO_CHANGE).stayIn(State.SEEKING),

        // Seeking
        on(Event.SEEK).stayIn(State.SEEKING),
        on(Event.SEEK).stayIn(State.PAUSE),
        on(Event.SEEK).stayIn(State.QUALITY_CHANGE),
        on(Event.SEEK).stayIn(State.REBUFFERING),
        on(Event.SEEK).stayIn(State.ERROR),
        on(Event.SEEK).stayIn(State.STARTUP),
        on(Event.SEEK).stayIn(State.READY),
        on(Event.SEEK).stayIn(State.VIDEOSTART_FAILED),
        on(Event.SEEKED).stayIn(State.PAUSE),
        on(Event.SEEKED).stayIn(State.QUALITY_CHANGE),
        on(Event.SEEKED).stayIn(State.REBUFFERING),
        on(Event.SEEKED).stayIn(State.ERROR),
        on(Event.SEEKED).stayIn(State.STARTUP),
        on(Event.SEEKED).stayIn(State.READY),
        on(Event.SEEKED).stayIn(State.VIDEOSTART_FAILED),
      ],

      // Keep callbacks sorted by order in which they are called by JSM library (and after order, sorted by name)
      //
      // Do not use convenience, shortened callbacks
      //  - instead of `onEVENT` use `onafterEVENT`
      //  - instead of `onSTATE` use `onenterSTATE`
      //
      // see: https://github.com/jakesgordon/javascript-state-machine/blob/2.4.0/README.md#callbacks
      callbacks: {
        onleavestate: (
          event: string | undefined,
          from: string | undefined,
          to: string | undefined,
          eventTimestamp: number,
          eventData: AnalyticsEventBase,
        ) => {
          // Skip this JSM callback when one of the JSM callback arguments is "nullish".
          // Callback arguments `event, from, to` should always be defined, but JSM types say they can be `undefined`,
          // so we have to use type predicates to check if they are not nullish to avoid TypeScript errors
          if (isNullish(event) || isNullish(from) || isNullish(to)) {
            return;
          }

          logStateMachineTransition('LEAVE', event, from, to);

          // Skip this JSM callback for JSM initial event
          // This event is fired when the state machine is constructed,
          // to transition us from 'none' to custom initial 'SETUP' state
          if (event === JSM_INITIAL_EVENT) {
            return;
          }

          // Skip this JSM callback when an JSM event without a timestamp is called,
          // because it would cause NPE in analytics code (state duration calculation etc.).
          // This probably has happened because the state machine was not used correctly,
          // aka. JSM event was not called via `callEvent` method as it should be!
          if (!eventTimestamp) {
            return;
          }

          if (eventData) {
            this.stateMachineCallbacks.setVideoTimeEndFromEvent(eventData);
          }

          const stateDuration = eventTimestamp - this.onEnterStateTimestamp;
          this.getStateHandler()[from].onleavestate(stateDuration, to, event);
        },

        onenterstate: (event, from, to, eventTimestamp: number, eventData: AnalyticsEventBase) => {
          // Skip this JSM callback when one of the JSM callback arguments is "nullish".
          // Callback arguments `event, from, to` should always be defined, but JSM types say they can be undefined,
          // so we have to use type predicates to check if they are not nullish to avoid TypeScript errors
          if (isNullish(event) || isNullish(from) || isNullish(to)) {
            return;
          }

          logStateMachineTransition('ENTER', event, from, to);

          this.onEnterStateTimestamp = eventTimestamp ?? Date.now();
          // if customer set custom "start time" timestamp in AnalyticsStateMachineOptions of adapter constructor,
          // use it and override `onEnterStateTimestamp` during JSM initialization (transition to initial SETUP state)
          if (isNumber(opts.starttime) && event === JSM_INITIAL_EVENT) {
            this.onEnterStateTimestamp = opts.starttime;
          }

          if (eventData) {
            this.stateMachineCallbacks.setVideoTimeStartFromEvent(eventData);
          }

          this.getStateHandler()[to].onenterstate(event, eventData);
        },
      },
    });
  }

  override onSsaiPlaybackInteraction(_timestamp: number, _eventObject: AnalyticsEventBase): void {
    // TODO: [AN-4290] Implement SSAI ad handling for AmazonIVS
    //  See: https://bitmovin.atlassian.net/browse/AN-4290
  }

  private getStateHandler = (): StateHandlerList => {
    return {
      [State.SETUP]: {
        onenterstate: () => undefined,
        onleavestate: (duration: number) => {
          this.stateMachineCallbacks.setup(duration, State.SETUP.toLowerCase());
        },
      },
      [State.READY]: {
        onenterstate: () => undefined,
        onleavestate: () => undefined,
      },
      [State.STARTUP]: {
        onenterstate: () => {
          this.setVideoStartTimeout();
        },
        onleavestate: (duration, to, event) => {
          this.clearVideoStartTimeout();
          if (to == State.PLAYING) {
            this.stateMachineCallbacks.startup(duration, State.STARTUP.toLowerCase());
          }

          if (to == State.VIDEOSTART_FAILED) {
            this.stateMachineCallbacks.videoStartFailed(duration, createVideoStartFailedReason(event));
          }
        },
      },
      [State.PLAYING]: {
        onenterstate: () => {
          this.playingHeartbeatService.startHeartbeat();
        },
        onleavestate: (duration) => {
          this.playingHeartbeatService.stopHeartbeat();
          this.stateMachineCallbacks.playing(duration, State.PLAYING.toLowerCase());
        },
      },
      [State.PAUSE]: {
        onenterstate: () => undefined,
        onleavestate: (duration) => {
          this.stateMachineCallbacks.pause(duration, State.PAUSE.toLowerCase());
        },
      },
      [State.REBUFFERING]: {
        onenterstate: () => {
          this.startRebufferingHeartbeatInterval();
        },
        onleavestate: (duration) => {
          this.resetRebufferingHelpers();
          this.stateMachineCallbacks.rebuffering(duration, State.REBUFFERING.toLowerCase());
        },
      },
      [State.VIDEOSTART_FAILED]: {
        onenterstate: (event: string, eventData: AnalyticsEventBase) => {
          if (event === Event.ERROR) {
            this.stateMachineCallbacks.error(eventData as ErrorEvent);
          }
        },
        onleavestate: () => undefined,
      },
      [State.ERROR]: {
        onenterstate: (_event: string, eventData: AnalyticsEventBase) => {
          this.stateMachineCallbacks.error(eventData as ErrorEvent);
        },
        onleavestate: () => undefined,
      },
      [State.UNLOADED]: {
        onenterstate: () => {
          this.stateMachineCallbacks.unload(0, State.UNLOADED);
        },
        onleavestate: () => undefined,
      },
      [State.QUALITY_CHANGE]: {
        onenterstate: (_event, eventData) => {
          this.stateMachine.FINISH_QUALITYCHANGE(this.onEnterStateTimestamp, eventData);
        },
        onleavestate: () => {
          this.stateMachineCallbacks.qualitychange(0, State.QUALITY_CHANGE.toLowerCase());
        },
      },
      [State.SEEKING]: {
        onenterstate: () => undefined,
        onleavestate: (duration) => {
          this.stateMachineCallbacks.end_play_seeking(duration, State.SEEKING.toLowerCase());
        },
      },
    };
  };
}

const getAllStatesBut = (states: string[]) => {
  return getAllStates().filter((i) => states.indexOf(i) < 0);
};

const getAllStates = () => {
  return Object.keys(State).map((key) => State[key]);
};

type StateHandler = {
  onenterstate: (event: string, eventData: AnalyticsEventBase) => void;
  onleavestate: (duration: number, to: string, event: string) => void;
};

type StateHandlerList = Record<State, StateHandler>;
